import React, { useState } from 'react';
import { Link, Routes, Route, useLocation } from 'react-router-dom';
import PersonalDetails from './PersonalDetails';
import LoginSecurity from './LoginSecurity';
import './AccountManagement.css';

const ComingSoon = ({ title, icon, description }) => {
  const [notified, setNotified] = useState(false);

  return (
    <div className="coming-soon-card">
      <div className="coming-soon-visual">
        <span className="cs-ring cs-ring-1" aria-hidden="true" />
        <span className="cs-ring cs-ring-2" aria-hidden="true" />
        <div className="coming-soon-icon">
          <i className={`fa ${icon}`} aria-hidden="true" />
        </div>
      </div>
      <span className="coming-soon-badge">
        <i className="fa fa-hourglass-half" aria-hidden="true" /> Coming soon
      </span>
      <h2 className="coming-soon-title">{title}</h2>
      <p className="coming-soon-desc">{description}</p>
      <button
        type="button"
        className={`coming-soon-btn ${notified ? 'notified' : ''}`}
        onClick={() => setNotified(true)}
      >
        {notified ? (
          <>
            <i className="fa fa-check" aria-hidden="true" /> You&apos;re on the list!
          </>
        ) : (
          <>
            <i className="fa fa-bell" aria-hidden="true" /> Notify me
          </>
        )}
      </button>
    </div>
  );
};

const AccountManagement = () => {
  const location = useLocation();

  const sidebarItems = [
    {
      path: '/accountbilling/personal-details',
      label: 'Personal Info',
      icon: 'fa-user',
      component: <PersonalDetails />,
      accent: 'blue',
    },
    {
      path: '/accountbilling/login-security',
      label: 'Login & Security',
      icon: 'fa-shield-alt',
      component: <LoginSecurity />,
      accent: 'green',
    },
    {
      path: '/accountbilling/payments',
      label: 'Payments & Payouts',
      icon: 'fa-wallet',
      accent: 'purple',
      component: (
        <ComingSoon
          title="Payments & Payouts"
          icon="fa-wallet"
          description="Soon you'll be able to manage your payment methods, track transactions and request payouts — all in one place."
        />
      ),
    },
    {
      path: '/accountbilling/notifications',
      label: 'Notifications',
      icon: 'fa-bell',
      accent: 'yellow',
      component: (
        <ComingSoon
          title="Notifications"
          icon="fa-bell"
          description="Customize what you hear about — new messages, booking updates, offers and important alerts from Spotfli."
        />
      ),
    },
    {
      path: '/accountbilling/privacy',
      label: 'Privacy & Sharing',
      icon: 'fa-lock',
      accent: 'red',
      component: (
        <ComingSoon
          title="Privacy & Sharing"
          icon="fa-lock"
          description="Take control of your data: manage what you share, who can see your profile and how Spotfli uses your information."
        />
      ),
    },
    {
      path: '/accountbilling/preferences',
      label: 'Global Preferences',
      icon: 'fas fa-sliders-h',
      accent: 'teal',
      component: (
        <ComingSoon
          title="Global Preferences"
          icon="fas fa-sliders-h"
          description="Set your language, currency, region and display preferences so Spotfli feels just right for you."
        />
      ),
    },
  ];

  const getCurrentPageTitle = () => {
    const currentItem = sidebarItems.find(item => item.path === location.pathname);
    return currentItem ? currentItem.label : 'Account Management';
  };

  return (
    <div className="account-management-page">
      <div className="ab-page-bg" aria-hidden="true">
        <div className="ab-blob ab-blob-1" />
        <div className="ab-blob ab-blob-2" />
        <div className="ab-blob ab-blob-3" />
      </div>

      <div className="container account-management-container">
        <Link to="/accountbilling" className="back-to-billing">
          <i className="fa fa-arrow-left" aria-hidden="true"></i>
          Back to Account & Billing
        </Link>

        <div className="account-management-layout">
          {/* Sidebar Navigation */}
          <aside className="account-sidebar">
            <div className="sidebar-header">
              <div className="sidebar-header-icon">
                <i className="fa fa-cog" aria-hidden="true"></i>
              </div>
              <div>
                <h3>Account Settings</h3>
                <p>{getCurrentPageTitle()}</p>
              </div>
            </div>
            <nav aria-label="Account settings">
              <ul className="sidebar-nav">
                {sidebarItems.map((item) => (
                  <li key={item.path} className="sidebar-nav-item">
                    <Link
                      to={item.path}
                      className={`sidebar-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                    >
                      <span className={`nav-icon nav-accent-${item.accent}`}>
                        <i className={`fa ${item.icon}`}></i>
                      </span>
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="sidebar-footer">
              <i className="fa fa-shield" aria-hidden="true"></i>
              Your data is protected with bank-level encryption.
            </div>
          </aside>

          {/* Main Content */}
          <main className="account-main-content">
            <Routes>
              {sidebarItems.map((item) => (
                <Route
                  key={item.path}
                  path={item.path.replace('/accountbilling', '')}
                  element={item.component}
                />
              ))}
              {/* Default route */}
              <Route path="/" element={<PersonalDetails />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AccountManagement;
